import * as api from "api/stockApi"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  createVehicleStock,
  deleteVehicleStock,
  getVehicleStocksByLocation,
  verifyFullChassis,
} from "api/stockApi"
import {
  useEditResource,
  usePaginatedResource,
  useResourceEditItem,
  useResourceItem,
} from "./useCrudResource"

export const VEHICLE_STOCK_QUERY_KEY = "vehicleStocks"
export const VEHICLE_OPTIONS_QUERY_KEY = "vehicleOptions"

// Fetch all vehicle stocks with optional params (pagination, filters)
export const useVehicleStocks = (params, enabled) =>
  usePaginatedResource(
    VEHICLE_STOCK_QUERY_KEY,
    api.getVehicleStocks,
    params,
    enabled
  )

// Generate pdf vehicle stocks with optional params (pagination, filters)
export const useVehicleStocksPdf = (params, enabled) =>
  usePaginatedResource(
    VEHICLE_STOCK_QUERY_KEY + "Pdf",
    api.getVehicleStocksPdf,
    params,
    enabled
  )

// Vehicle movements
export const useVehicleStockMovements = (params, enabled) =>
  usePaginatedResource(
    VEHICLE_STOCK_QUERY_KEY + "Movements",
    api.getVehicleStockMovements,
    params,
    enabled
  )

export const useVehicleOptions = (params, enabled) =>
  usePaginatedResource(
    VEHICLE_OPTIONS_QUERY_KEY,
    api.getVehicleOptions,
    params,
    enabled
  )

// Fetch a single vehicle stock by ID
export const useVehicleStock = id =>
  useResourceItem(VEHICLE_STOCK_QUERY_KEY, api.getVehicleStock, id)

// Fetch a single vehicle edit stock by ID
export const useVehicleEditStock = id =>
  useResourceEditItem(VEHICLE_STOCK_QUERY_KEY, api.getVehicleStock, id)

// Fetch vehicle stocks by location ID
export const useVehicleStocksByLocation = (
  locationId,
  params = {},
  enabled = true
) => {
  return useQuery({
    queryKey: [VEHICLE_STOCK_QUERY_KEY, "location", locationId],
    queryFn: () => getVehicleStocksByLocation(locationId, params),
    keepPreviousData: true,
    enabled: !!locationId && enabled,
  })
}

// Create a new vehicle stock
export const useCreateVehicleStock = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createVehicleStock,
    onSuccess: data => {
      if (data?.success) {
        queryClient.invalidateQueries([VEHICLE_STOCK_QUERY_KEY, "create"])
      }
    },
  })
}

// Quick-onboard a standalone vehicle from a vehicle_info payload
export const useOnboardVehicleStock = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: api.onboardVehicleStock,
    onSuccess: data => {
      if (data?.success) {
        queryClient.invalidateQueries([VEHICLE_STOCK_QUERY_KEY, "create"])
      }
    },
  })
}

// Create bulk vehicle stock
export const useCreateBulkVehicleStock = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: api.createBulkVehicleStock,
    onSuccess: data => {
      if (data?.success) {
        queryClient.invalidateQueries([VEHICLE_STOCK_QUERY_KEY, "create"])
      }
    },
  })
}

// Tag importer by full chassis/stock id
export const useTagImporter = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: api.tagImporter,
    onSuccess: data => {
      if (data?.success) {
        queryClient.invalidateQueries([VEHICLE_STOCK_QUERY_KEY, "tag-importer"])
      }
    },
  })
}

// Bulk update status, draft/publish state and publishable platforms by chassis
export const useBulkUpdateStockStatusPublish = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: api.bulkUpdateStockStatusPublish,
    onSuccess: data => {
      if (data?.success) {
        queryClient.invalidateQueries([VEHICLE_STOCK_QUERY_KEY])
      }
    },
  })
}

// Edit/update an existing vehicle stock
export const useEditVehicleStock = () =>
  useEditResource(api.editVehicleStock, VEHICLE_STOCK_QUERY_KEY)

// Delete a vehicle stock
export const useDeleteVehicleStock = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteVehicleStock,
    onSuccess: data => {
      if (data?.success) {
        queryClient.invalidateQueries([VEHICLE_STOCK_QUERY_KEY, "delete"])
      }
    },
  })
}

// Verify a vehicle stock by full chassis
export const useVerifyFullChassis = () => {
  return useMutation({
    mutationFn: fullChassis => verifyFullChassis(fullChassis),
    onError: error => {
      console.error("Error verifying full chassis:", error)
    },
  })
}

// Search vehicle
export const useVehicleSearchQuery = (
  searchTerm,
  { all = true, limit = 10, enabled = true } = {}
) => {
  return useQuery({
    queryKey: ["vehicle-searching", { search: searchTerm, all, limit }],
    queryFn: () =>
      api.getVehicleStocks({
        search: searchTerm,
        all: true,
        limit: limit,
      }),
    enabled: Boolean(enabled && searchTerm && searchTerm.length >= 2),
    select: data => {
      const vehicles = data?.data?.vehicles
      return vehicles ?? []
    },
    staleTime: 5 * 60 * 1000, // 5 minutes cache
  })
}
